import React, { useCallback, useMemo, useRef } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import loadable from '@loadable/component';
import PDPShownIn from 'components/consumer/PDP/PDPShownIn';
import convertImgixPropsToQueryParams from 'commons/convertImgixPropsToQueryParams';
import { galleryCommonImgixProps, GALLERY_IMAGE_WIDTH_ZOOM } from '../PDPDesktopFullScreenGallery2/galleryCommonImgixProps';
import 'react-photoswipe/lib/photoswipe.css';
const PhotoSwipe = loadable(() => import('react-photoswipe'));

// galleryCommonImgixProps fixes the aspect ratio at 3:2, so the size we
// declare to PhotoSwipe matches the image imgix actually returns.
const PINCH_ZOOM_IMAGE_WIDTH = GALLERY_IMAGE_WIDTH_ZOOM;
const PINCH_ZOOM_IMAGE_HEIGHT = PINCH_ZOOM_IMAGE_WIDTH / 3 * 2;
const haveSameSrcs = (prevItems, nextItems) => prevItems.length === nextItems.length && prevItems.every((item, index) => item.src === nextItems[index].src);

// react-photoswipe reloads every slide on any prop change while open, so
// this stops selectedPhotoIndex updates from reloading the slide mid-swipe.
// Items are compared by src, since the photos array is rebuilt upstream
// whenever the broken-image list changes.
const MemoizedPhotoSwipe = React.memo(PhotoSwipe, (prevProps, nextProps) => prevProps.isOpen === nextProps.isOpen && haveSameSrcs(prevProps.items, nextProps.items));
const PDPPinchToZoomGallery = ({
  isOpen = false,
  isEditOrder = false,
  photos = [],
  selectedPhotoIndex,
  setSelectedPhotoIndex,
  onClose,
  onPhotoViewed
}) => {
  // src is a bare imgix URL; without a width PhotoSwipe pulled the full
  // original. Strip any existing query string first, since this always adds one.
  // Same params as the desktop lightbox; crop data is deliberately not applied
  // there either, since it shows a small window of the photo (CON-8753).
  const items = useMemo(() => photos.map(({
    src
  }) => ({
    src: convertImgixPropsToQueryParams({
      ...galleryCommonImgixProps,
      width: PINCH_ZOOM_IMAGE_WIDTH
    }, src?.split('?')[0]),
    w: PINCH_ZOOM_IMAGE_WIDTH,
    h: PINCH_ZOOM_IMAGE_HEIGHT
  })), [photos]);

  // options.index is only read on open, so keep it out of the memo above.
  const openingIndexRef = useRef(selectedPhotoIndex);
  if (isOpen) {
    openingIndexRef.current = selectedPhotoIndex;
  }

  // Stable callback identities for the memo above, reading current values.
  const latestRef = useRef();
  latestRef.current = {
    photos,
    setSelectedPhotoIndex,
    onClose,
    onPhotoViewed
  };
  const currentIndexRef = useRef(selectedPhotoIndex);
  currentIndexRef.current = selectedPhotoIndex;
  const handleClose = useCallback((...args) => latestRef.current.onClose(...args), []);
  // updatedData is the PhotoSwipe instance itself (react-photoswipe unshifts
  // `this` before calling listeners).
  const handleAfterChange = useCallback(updatedData => {
    if (updatedData && typeof updatedData.getCurrentIndex === 'function') {
      const updatedSelectedIndex = updatedData.getCurrentIndex();
      // photos can shrink while open (broken images get removed) and
      // PhotoSwipe may still report the old index; storing it blanks the footer
      if (updatedSelectedIndex < 0 || updatedSelectedIndex >= latestRef.current.photos.length) {
        return;
      }
      const previousIndex = currentIndexRef.current;
      currentIndexRef.current = updatedSelectedIndex;
      latestRef.current.setSelectedPhotoIndex(updatedSelectedIndex);

      // Only a real swipe: PhotoSwipe also reports the photo it opened on
      if (updatedSelectedIndex !== previousIndex) {
        latestRef.current.onPhotoViewed?.(latestRef.current.photos[updatedSelectedIndex]);
      }
    }
  }, []);
  return <div className="PDPPinchToZoomGallery">
      <MemoizedPhotoSwipe className="PDPPinchToZoomGallery__PhotoSwipe z-[1] [&_img]:object-contain" isOpen={isOpen} items={items} options={{
      index: openingIndexRef.current,
      history: false
    }} onClose={handleClose} afterChange={handleAfterChange} />

      <div className={classNames('z-[2000] fixed bottom-0 left-0 right-0 p-4 ', {
      hidden: !isOpen,
      flex: isOpen
    })}>
        <PDPShownIn fullWidth isEditOrder={isEditOrder} optionId={photos?.[selectedPhotoIndex]?.optionId} valueId={photos?.[selectedPhotoIndex]?.valueId} upholsteryName={photos?.[selectedPhotoIndex]?.upholsteryName} onSelection={() => onClose(true)} />
      </div>
    </div>;
};
PDPPinchToZoomGallery.propTypes = {
  isOpen: PropTypes.bool,
  isEditOrder: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  photos: PropTypes.arrayOf(PropTypes.shape({
    src: PropTypes.string,
    id: PropTypes.string,
    type: PropTypes.string
  })),
  onPhotoViewed: PropTypes.func,
  selectedPhotoIndex: PropTypes.number,
  setSelectedPhotoIndex: PropTypes.func
};
export default PDPPinchToZoomGallery;